import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Upload, X, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

// Lets a customer report inappropriate conduct by a collector. No confirmed contact required.
export function ReportConductDialog({ leadId, token, collectorName }) {
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!explanation.trim()) { toast.error("Please describe what happened"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("lead_id", leadId);
      fd.append("explanation", explanation);
      if (token) fd.append("token", token);
      files.forEach((f) => fd.append("files", f));
      await api.post("/reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report submitted. Our team will review it.");
      setOpen(false);
      setExplanation("");
      setFiles([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`report-conduct-open-${leadId}`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#B91C1C] hover:underline underline-offset-2"
        >
          <Flag className="w-3.5 h-3.5" /> Report inappropriate conduct
        </button>
      </DialogTrigger>
      <DialogContent data-testid="report-conduct-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1E3F33] flex items-center gap-2">
            <Flag className="w-5 h-5 text-[#B91C1C]" /> Report a concern
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            Report inappropriate conduct by <span className="font-medium text-[#1E3F33]">{collectorName || "this collector"}</span>. Please describe what happened and attach any proof.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <textarea
            data-testid="report-conduct-explanation"
            required
            rows={5}
            className="input-soft"
            placeholder="Describe the inappropriate conduct in detail…"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
          />
          <div>
            <input ref={inputRef} data-testid="report-conduct-file-input" type="file" multiple accept="application/pdf,image/*" onChange={addFiles} className="hidden" />
            <button type="button" data-testid="report-conduct-attach" onClick={() => inputRef.current?.click()} className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm">
              <Upload className="w-4 h-4" /> Attach documents or pictures
            </button>
            {files.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="report-conduct-file-list">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-[#1E3F33]">
                    <span className="inline-flex items-center gap-1.5 truncate"><FileText className="w-3.5 h-3.5 shrink-0" /> {f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-[#8A9A86] hover:text-[#B91C1C] shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" data-testid="report-conduct-submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Submitting…" : "Submit report"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
